import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Divider,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { Save, Refresh } from '@mui/icons-material';

import { useAuthStore } from '../../store/authStore';
import { useTranslation } from '../../store/languageStore';
import authService from '../../shared/services/authService';
import codelistService from '../../services/codelistService';

/**
 * ATLAS Customs Configuration section.
 *
 * Self-contained: owns the ATLAS config form state, its load effect and save handler.
 * Extracted from ProfilePage so it can live on the Settings page. Gated for admins
 * by the parent (Settings page does not render this for admin users).
 */
const AtlasConfigSection: React.FC = () => {
  const { user } = useAuthStore();
  const { t, tString } = useTranslation();

  const [atlasConfig, setAtlasConfig] = useState({
    atlas_user_id: '',
    atlas_eori_number: '',
    atlas_customs_office: 'DE005000',
    atlas_gestellungszollstelle: '',
    atlas_ausfuhrzollstelle: '',
    company_name: '',
    company_street: '',
    company_postal_code: '',
    company_city: '',
    company_country: 'DE',
    default_currency: 'EUR',
    default_incoterm: 'EXW',
    default_country_origin: 'DE',
    default_transport_mode: '3',
    // How this company FILES. On no commercial document, the same on every
    // shipment, and not predictable — so the user tells us once. No default
    // value: choosing a customs procedure for someone would be a false
    // declaration.
    atlas_declaration_type: '',
    atlas_participant_constellation: '',
    atlas_requested_procedure: '',
    atlas_previous_procedure: '',
    atlas_bewilligung_art: '',
    atlas_bewilligung_referenznummer: '',
  });
  // Options come from the company's OWN ATLAS codelist (getOptionsForCompany),
  // never hardcoded: the permitted set differs per customer, and three public
  // write-ups of these codes disagreed with each other and with ATLAS. The
  // descriptions ATLAS returns are used verbatim as the option labels.
  const [declTypeOptions, setDeclTypeOptions] = useState<Array<{code: string; description: string}>>([]);
  const [constellationOptions, setConstellationOptions] = useState<Array<{code: string; description: string}>>([]);
  const [atlasConfigLoading, setAtlasConfigLoading] = useState(true);
  const [isAtlasComplete, setIsAtlasComplete] = useState(false);
  const [atlasSaving, setAtlasSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [codelistRefreshing, setCodelistRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);
  const [codelistMessage, setCodelistMessage] = useState<string | null>(null);

  // Load ATLAS configuration on mount
  useEffect(() => {
    const loadAtlasConfig = async () => {
      try {
        setAtlasConfigLoading(true);
        const config = await authService.getAtlasConfig();
        setAtlasConfig({
          atlas_user_id: config.atlas_user_id || '',
          atlas_eori_number: config.atlas_eori_number || '',
          atlas_customs_office: config.atlas_customs_office || 'DE005000',
          atlas_gestellungszollstelle: config.atlas_gestellungszollstelle || '',
          atlas_ausfuhrzollstelle: config.atlas_ausfuhrzollstelle || '',
          company_name: config.company_name || '',
          company_street: config.company_street || '',
          company_postal_code: config.company_postal_code || '',
          company_city: config.company_city || '',
          company_country: config.company_country || 'DE',
          default_currency: config.default_currency || 'EUR',
          default_incoterm: config.default_incoterm || 'EXW',
          default_country_origin: config.default_country_origin || 'DE',
          default_transport_mode: config.default_transport_mode || '3',
          atlas_declaration_type: config.atlas_declaration_type || '',
          atlas_participant_constellation: config.atlas_participant_constellation || '',
          atlas_requested_procedure: config.atlas_requested_procedure || '',
          atlas_previous_procedure: config.atlas_previous_procedure || '',
          atlas_bewilligung_art: config.atlas_bewilligung_art || '',
          atlas_bewilligung_referenznummer: config.atlas_bewilligung_referenznummer || '',
        });
        setIsAtlasComplete(config.is_complete || false);
      } catch (error) {
        console.error('Failed to load ATLAS config:', error);
      } finally {
        setAtlasConfigLoading(false);
      }
    };

    const loadFilingOptions = async () => {
      // A0121 = Art der Ausfuhranmeldung, A0127 = Beteiligtenkonstellation.
      // Failing to load leaves the dropdowns empty rather than falling back to
      // hardcoded codes — an invented option here becomes a false declaration.
      try {
        const lists = await codelistService.getMultipleCodelists(['A0121', 'A0127']);
        const toOptions = (id: string) =>
          ((lists?.[id] as any)?.values || (lists?.[id] as any)?.items || [])
            .map((v: any) => ({ code: String(v.code ?? ''), description: String(v.description ?? '') }))
            .filter((o: any) => o.code);
        setDeclTypeOptions(toOptions('A0121'));
        setConstellationOptions(toOptions('A0127'));
      } catch (error) {
        console.error('Could not load ATLAS filing options:', error);
      }
    };

    if (user) {
      loadAtlasConfig();
      loadFilingOptions();
    }
  }, [user]);

  // Save ATLAS configuration
  const handleSaveAtlasConfig = async () => {
    try {
      setAtlasSaving(true);
      setError(null);
      setSuccessMessage(null);

      const result = await authService.updateAtlasConfig(atlasConfig);
      setSuccessMessage('ATLAS configuration saved successfully!');
      setIsAtlasComplete(result.is_complete);

      // Reload config to get validated values
      const updatedConfig = await authService.getAtlasConfig();
      setAtlasConfig({
        atlas_user_id: updatedConfig.atlas_user_id || '',
        atlas_eori_number: updatedConfig.atlas_eori_number || '',
        atlas_customs_office: updatedConfig.atlas_customs_office || 'DE005000',
        atlas_gestellungszollstelle: updatedConfig.atlas_gestellungszollstelle || '',
        atlas_ausfuhrzollstelle: updatedConfig.atlas_ausfuhrzollstelle || '',
        company_name: updatedConfig.company_name || '',
        company_street: updatedConfig.company_street || '',
        company_postal_code: updatedConfig.company_postal_code || '',
        company_city: updatedConfig.company_city || '',
        company_country: updatedConfig.company_country || 'DE',
        default_currency: updatedConfig.default_currency || 'EUR',
        default_incoterm: updatedConfig.default_incoterm || 'EXW',
        default_country_origin: updatedConfig.default_country_origin || 'DE',
        default_transport_mode: updatedConfig.default_transport_mode || '3',
        atlas_declaration_type: updatedConfig.atlas_declaration_type || '',
        atlas_participant_constellation: updatedConfig.atlas_participant_constellation || '',
        atlas_requested_procedure: updatedConfig.atlas_requested_procedure || '',
        atlas_previous_procedure: updatedConfig.atlas_previous_procedure || '',
        atlas_bewilligung_art: updatedConfig.atlas_bewilligung_art || '',
        atlas_bewilligung_referenznummer: updatedConfig.atlas_bewilligung_referenznummer || '',
      });

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save ATLAS configuration');
      console.error('ATLAS config save error:', err);
    } finally {
      setAtlasSaving(false);
    }
  };

  useEffect(() => {
    authService.getCodelistLastRefreshed().then((data: any) => setLastRefreshed(data?.lastRefreshed || null)).catch(() => {});
  }, []);

  const handleCodelistRefresh = async () => {
    setCodelistRefreshing(true);
    setCodelistMessage(null);
    try {
      await authService.refreshCodelists();
      await codelistService.forceRefresh();
      const data = await authService.getCodelistLastRefreshed();
      setLastRefreshed(data?.lastRefreshed || null);
      setCodelistMessage('Codelists refreshed successfully!');
      setTimeout(() => setCodelistMessage(null), 3000);
    } catch (err: any) {
      setCodelistMessage(err.response?.data?.detail || 'Failed to refresh codelists');
    } finally {
      setCodelistRefreshing(false);
    }
  };

  return (
    <>
    {/* Codelist Refresh — separate card above Atlas config */}
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6" fontWeight={600}>
          Codelist Data
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Fetch the latest customs codelists (currencies, countries, customs offices, etc.) from ATLAS.
      </Typography>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Tooltip title="Fetch the latest customs codelists from ATLAS">
          <span>
            <Button
              variant="outlined"
              startIcon={codelistRefreshing ? <CircularProgress size={18} /> : <Refresh />}
              onClick={handleCodelistRefresh}
              disabled={codelistRefreshing}
            >
              {codelistRefreshing ? 'Refreshing...' : 'Refresh Codelists'}
            </Button>
          </span>
        </Tooltip>
        {lastRefreshed && (
          <Typography variant="caption" color="text.secondary">
            Last refreshed: {new Date(lastRefreshed).toLocaleString()}
          </Typography>
        )}
        {codelistMessage && (
          <Alert severity={codelistMessage.includes('success') ? 'success' : 'error'} sx={{ flex: 1 }}>
            {codelistMessage}
          </Alert>
        )}
      </Box>
    </Paper>

    <Paper elevation={2} sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={600}>
          ATLAS Customs Configuration
        </Typography>
        {atlasConfigLoading ? (
          <CircularProgress size={20} />
        ) : isAtlasComplete ? (
          <Chip label={tString('profile.complete')} color="success" size="small" />
        ) : (
          <Chip label={tString('profile.incomplete')} color="warning" size="small" />
        )}
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        {t('profile.atlasConfigDescription')}
      </Alert>

      {atlasConfigLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* ATLAS Credentials */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary" fontWeight={600} gutterBottom>
              ATLAS Credentials
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              id="atlas-config-user-id"
              data-field-name="atlas_user_id"
              fullWidth
              label="ATLAS User ID"
              value={atlasConfig.atlas_user_id}
              onChange={(e) => setAtlasConfig({...atlasConfig, atlas_user_id: e.target.value})}
              helperText="Your company's ATLAS user ID"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              id="atlas-config-eori-number"
              data-field-name="atlas_eori_number"
              fullWidth
              label="EORI Number"
              value={atlasConfig.atlas_eori_number}
              onChange={(e) => setAtlasConfig({...atlasConfig, atlas_eori_number: e.target.value})}
              helperText="Country code + up to 15 letters/digits (e.g., DE123456789012)"
              /* Matches the EU Economic Operators format the backend validates
                 against. The previous /^[A-Z]{2}[0-9]{12,15}$/ was digits-only
                 with a 12-character minimum, which flagged valid EORIs as
                 errors — a German number built from a 7-digit Zollnummer
                 (DE7499604), or any EORI containing letters (NL812188159B01). */
              error={atlasConfig.atlas_eori_number !== '' && !/^[A-Z]{2}[A-Z0-9]{1,15}$/.test(atlasConfig.atlas_eori_number.replace(/\s+/g, '').toUpperCase())}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              id="atlas-config-customs-office"
              data-field-name="atlas_customs_office"
              fullWidth
              label="Customs Office"
              value={atlasConfig.atlas_customs_office}
              onChange={(e) => setAtlasConfig({...atlasConfig, atlas_customs_office: e.target.value})}
              helperText="8 characters (e.g., DE005000)"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              id="atlas-config-declaration-office"
              data-field-name="atlas_gestellungszollstelle"
              fullWidth
              label="Declaration Office"
              value={atlasConfig.atlas_gestellungszollstelle}
              onChange={(e) => setAtlasConfig({...atlasConfig, atlas_gestellungszollstelle: e.target.value})}
              helperText="Optional - Gestellungszollstelle"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              id="atlas-config-export-office"
              data-field-name="atlas_ausfuhrzollstelle"
              fullWidth
              label="Export Office"
              value={atlasConfig.atlas_ausfuhrzollstelle}
              onChange={(e) => setAtlasConfig({...atlasConfig, atlas_ausfuhrzollstelle: e.target.value})}
              helperText="Optional - Ausfuhrzollstelle"
              size="small"
            />
          </Grid>

          {/* Company Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
              Company Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <TextField
              id="atlas-config-company-name"
              data-field-name="company_name"
              fullWidth
              label="Company Name"
              value={atlasConfig.company_name}
              onChange={(e) => setAtlasConfig({...atlasConfig, company_name: e.target.value})}
              required
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              id="atlas-config-company-street"
              data-field-name="company_street"
              fullWidth
              label="Street Address"
              value={atlasConfig.company_street}
              onChange={(e) => setAtlasConfig({...atlasConfig, company_street: e.target.value})}
              required
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              id="atlas-config-company-postal-code"
              data-field-name="company_postal_code"
              fullWidth
              label="Postal Code"
              value={atlasConfig.company_postal_code}
              onChange={(e) => setAtlasConfig({...atlasConfig, company_postal_code: e.target.value})}
              required
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              id="atlas-config-company-city"
              data-field-name="company_city"
              fullWidth
              label="City"
              value={atlasConfig.company_city}
              onChange={(e) => setAtlasConfig({...atlasConfig, company_city: e.target.value})}
              required
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              id="atlas-config-company-country"
              data-field-name="company_country"
              fullWidth
              label="Country Code"
              value={atlasConfig.company_country}
              onChange={(e) => setAtlasConfig({...atlasConfig, company_country: e.target.value.toUpperCase()})}
              inputProps={{ maxLength: 2 }}
              helperText="2-letter ISO (e.g., DE, US)"
              required
              size="small"
            />
          </Grid>

          {/* Default Preferences */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
              Default Preferences
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              id="atlas-config-default-currency"
              data-field-name="default_currency"
              select
              fullWidth
              label="Currency"
              value={atlasConfig.default_currency}
              onChange={(e) => setAtlasConfig({...atlasConfig, default_currency: e.target.value})}
              SelectProps={{ native: true }}
              size="small"
            >
              <option value="EUR">EUR - Euro</option>
              <option value="USD">USD - US Dollar</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="CHF">CHF - Swiss Franc</option>
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              id="atlas-config-default-incoterm"
              data-field-name="default_incoterm"
              select
              fullWidth
              label="Incoterm"
              value={atlasConfig.default_incoterm}
              onChange={(e) => setAtlasConfig({...atlasConfig, default_incoterm: e.target.value})}
              SelectProps={{ native: true }}
              size="small"
            >
              <option value="EXW">EXW - Ex Works</option>
              <option value="FOB">FOB - Free on Board</option>
              <option value="CIF">CIF - Cost, Insurance, Freight</option>
              <option value="DDP">DDP - Delivered Duty Paid</option>
              <option value="FCA">FCA - Free Carrier</option>
              <option value="CPT">CPT - Carriage Paid To</option>
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              id="atlas-config-default-country-origin"
              data-field-name="default_country_origin"
              select
              fullWidth
              label="Origin Country"
              value={atlasConfig.default_country_origin}
              onChange={(e) => setAtlasConfig({...atlasConfig, default_country_origin: e.target.value})}
              SelectProps={{ native: true }}
              size="small"
            >
              <option value="DE">Germany (DE)</option>
              <option value="US">USA (US)</option>
              <option value="CN">China (CN)</option>
              <option value="GB">UK (GB)</option>
              <option value="FR">France (FR)</option>
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              id="atlas-config-default-transport-mode"
              data-field-name="default_transport_mode"
              select
              fullWidth
              label="Transport Mode"
              value={atlasConfig.default_transport_mode}
              onChange={(e) => setAtlasConfig({...atlasConfig, default_transport_mode: e.target.value})}
              SelectProps={{ native: true }}
              size="small"
            >
              <option value="1">Sea</option>
              <option value="2">Rail</option>
              <option value="3">Road</option>
              <option value="4">Air</option>
              <option value="8">Inland Waterway</option>
            </TextField>
          </Grid>

          {/* ── How you file ──────────────────────────────────────────────
              These four are on no commercial document, are the same on every
              shipment, and cannot be predicted. Each was a validation error on
              all five reference documents until it was asked for here.

              Options come from this company's own ATLAS codelist, with ATLAS's
              descriptions used verbatim. Nothing is pre-selected: picking a
              customs procedure on someone's behalf would be a false
              declaration. */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
              How you file
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              These are the same for every shipment and appear on no document,
              so they are set once here. Your choice determines what else a
              declaration must contain.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              SelectProps={{ native: true }}
              id="atlas-config-declaration-type"
              data-field-name="atlas_declaration_type"
              fullWidth
              label="Export declaration type"
              value={atlasConfig.atlas_declaration_type}
              onChange={(e) => setAtlasConfig({...atlasConfig, atlas_declaration_type: e.target.value})}
              helperText={
                declTypeOptions.length === 0
                  ? 'Options unavailable — could not reach the ATLAS codelist'
                  : atlasConfig.atlas_declaration_type.startsWith('0000040')
                    || atlasConfig.atlas_declaration_type === '00001300'
                    ? 'A standard declaration: also needs your customs authorisation, nature of transaction, transport mode and intended office of exit'
                    : 'Determines what else the declaration must contain'
              }
              size="small"
            >
              <option value="">— not set —</option>
              {declTypeOptions.map(o => (
                <option key={o.code} value={o.code}>{o.code} — {o.description}</option>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              SelectProps={{ native: true }}
              id="atlas-config-participant-constellation"
              data-field-name="atlas_participant_constellation"
              fullWidth
              label="Participant constellation"
              value={atlasConfig.atlas_participant_constellation}
              onChange={(e) => setAtlasConfig({...atlasConfig, atlas_participant_constellation: e.target.value})}
              helperText={
                constellationOptions.length === 0
                  ? 'Options unavailable — could not reach the ATLAS codelist'
                  : atlasConfig.atlas_participant_constellation === '0011'
                    ? 'Also needs the representative AND the subcontractor'
                    : atlasConfig.atlas_participant_constellation === '0010'
                      ? 'Also needs your representative\u2019s details'
                      : atlasConfig.atlas_participant_constellation === '0001'
                        ? 'Also needs the subcontractor\u2019s address'
                        : 'Who declares, and whether a representative or subcontractor is involved'
              }
              size="small"
            >
              <option value="">— not set —</option>
              {constellationOptions.map(o => (
                <option key={o.code} value={o.code}>{o.code} — {o.description}</option>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              id="atlas-config-requested-procedure"
              data-field-name="atlas_requested_procedure"
              fullWidth
              label="Requested procedure"
              value={atlasConfig.atlas_requested_procedure}
              onChange={(e) => setAtlasConfig({...atlasConfig, atlas_requested_procedure: e.target.value})}
              helperText="Two digits. ATLAS publishes no codelist for this, so it is typed."
              error={atlasConfig.atlas_requested_procedure !== '' && !/^[0-9]{2}$/.test(atlasConfig.atlas_requested_procedure)}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              id="atlas-config-previous-procedure"
              data-field-name="atlas_previous_procedure"
              fullWidth
              label="Previous procedure"
              value={atlasConfig.atlas_previous_procedure}
              onChange={(e) => setAtlasConfig({...atlasConfig, atlas_previous_procedure: e.target.value})}
              helperText="Two digits. Applies to every line item."
              error={atlasConfig.atlas_previous_procedure !== '' && !/^[0-9]{2}$/.test(atlasConfig.atlas_previous_procedure)}
              size="small"
            />
          </Grid>

          {/* The company's customs authorisation. On no shipping document —
              settings is its only possible source, so without these two fields
              "Authorization / Reference Number: Required data field" could not
              be cleared except by retyping it on every declaration. */}
          <Grid item xs={12} md={6}>
            <TextField
              id="atlas-config-bewilligung-art"
              data-field-name="atlas_bewilligung_art"
              fullWidth
              label="Authorisation type"
              value={atlasConfig.atlas_bewilligung_art}
              onChange={(e) => setAtlasConfig({...atlasConfig, atlas_bewilligung_art: e.target.value.toUpperCase()})}
              helperText="Codelist S0615, e.g. C512 (simplified declaration procedure)."
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              id="atlas-config-bewilligung-ref"
              data-field-name="atlas_bewilligung_referenznummer"
              fullWidth
              label="Authorisation reference number"
              value={atlasConfig.atlas_bewilligung_referenznummer}
              onChange={(e) => setAtlasConfig({...atlasConfig, atlas_bewilligung_referenznummer: e.target.value.toUpperCase()})}
              error={
                atlasConfig.atlas_bewilligung_art.toUpperCase() === 'C512' &&
                atlasConfig.atlas_bewilligung_referenznummer !== '' &&
                atlasConfig.atlas_bewilligung_referenznummer.slice(2, 5) !== 'SDE'
              }
              helperText={
                atlasConfig.atlas_bewilligung_art.toUpperCase() === 'C512' &&
                atlasConfig.atlas_bewilligung_referenznummer !== '' &&
                atlasConfig.atlas_bewilligung_referenznummer.slice(2, 5) !== 'SDE'
                  ? `Characters 3-5 must read "SDE" for type C512 — yours read "${atlasConfig.atlas_bewilligung_referenznummer.slice(2, 5)}". ATLAS rejects it otherwise.`
                  : 'Your Bewilligung number from the Hauptzollamt. Filled in automatically on every declaration.'
              }
              size="small"
            />
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={2} mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveAtlasConfig}
                disabled={atlasSaving}
                startIcon={atlasSaving ? <CircularProgress size={20} /> : <Save />}
              >
                Save ATLAS Configuration
              </Button>
              {successMessage && (
                <Alert severity="success" sx={{ flex: 1 }}>
                  {successMessage}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ flex: 1 }}>
                  {error}
                </Alert>
              )}
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
    </>
  );
};

export default AtlasConfigSection;
